import React from 'react';
import { useCatSearchStore } from '../hooks/useCatSearchStore';
import { useCatSolr } from '../hooks/useCatSolr';
import { zfill } from '../common/catalogUtils';
import { Link, NavLink } from 'react-router-dom';
import ReactHtmlParser from 'react-html-parser';
import { HtmlCustom } from '../common/HtmlCustom';
import { useBiblStore } from '../hooks/useBiblStore';

/**
 * Returns the hit list for a specific result (generally text) in the search results
 *
 * @param bibid
 * @returns {JSX.Element|null}
 * @constructor
 */
export default function HitList({ doc }) {
    const search = useCatSearchStore();
    const collinfo = useBiblStore((state) => state.collinfo);
    const myedbib = collinfo?.colls[doc.coll]?.eds[doc.edsig]?.bibl;
    const bibid = doc.id;
    //console.log("bibl doc", doc);
    // TODO: figure out full list of hl_fields that work with query (can't do *)
    const hl_fields = [
        'pg_*',
        'title',
        'title_*',
        'text',
        'chaptitle',
        'agent',
        'colophon',
        'desc',
        'content',
        'content_bo',
    ];
    /*
    let qwrds = search.getQueryWords();
    console.log('query', search.getQuery(), qwrds);
    if (!Array.isArray(qwrds)) {
        qwrds = [qwrds];
    }

 */
    let query = search.getQuery();
    if (!query.includes(':')) {
        if (!query.includes('"')) {
            query = `"${query}"`;
        }
        query = `text:${query}`;
    }
    // console.log("hitlist query words", qwrds, qwrds.join('" OR "'));
    let fqs = `bibid_s:${bibid} AND -type:fulltext`;
    let docType = 'none';
    if (search?.isAdvanced) {
        docType = search?.advancedData?.docType;
        if (docType === 'bibl') {
            fqs += ` AND -type:textpg`;
        } else if (docType === 'text') {
            fqs += ` AND type:textpg`;
        }
    }

    // console.log('fqs', fqs);
    const qobj = {
        // q: '"' + qwrds.join('" OR "') + '"', // Since we are querying in the context of a known his look for text:querystr* for each item (row)
        q: query,
        fq: fqs,
        hl: true,
        'hl.fl': hl_fields.join(','),
        'hl.snippets': 10,
        'hl.fragsize': 100,
        rows: 30,
        sort: 'vnum_i asc, pnum_i asc, id asc',
    };
    // console.log("qobj", qobj);
    // For full text searches replace
    if (query.startsWith('text:')) {
        qobj.q = query.replace(/AND/g, 'OR');
    }
    // console.log('qobj', qobj);
    if (search?.hasFilters()) {
        // console.log("has filters");
        const newfq = [`bibid_s:${bibid}`];
        for (const [key, value] of Object.entries(search.filters)) {
            if (value.length > 0) {
                newfq.push(key + ':(' + value.join(' OR ') + ')');
            }
        }
        qobj.fq = newfq;
    }

    const {
        isLoading: isHitsLoading,
        data: hitsearch,
        isError: isHitsError,
        error: hitsError,
    } = useCatSolr(qobj);

    if (isHitsError || isHitsLoading) {
        return null;
    }

    const hltg = hitsearch?.highlighting;
    if (!hltg) {
        return null;
    }
    const hits = hltg;
    delete hits[bibid];
    // console.log({bibid: doc, hitsres: hitsearch, hits: hits});
    let m3;
    // console.log(bibid, {hitsearch: hitsearch, highlightkeys: Object.keys(hltg), hits: hits});
    return (
        <ol className="hitlist">
            {Object.keys(hits).map((okey, oki) => {
                const hok = hits[okey]; // hits for key
                // console.log("hit key and object", okey, hok);
                let hitres = hok;
                if (Array.isArray(hok)) {
                    hitres = hok[0];
                } else if (Object.keys(hok)?.length > 0) {
                    hitres = hok[Object.keys(hok)[0]];
                }
                const mtch = okey.match(
                    /([\w\d]+)-([\w\d]+)-([\w\d]+)-text-v(\d+)p([\dab]+)/
                );
                // Match for LCCW, e.g. lccw-0002-text-p23
                const mtch2 = okey.match(/([\w\d]+)-([\d]+)-text-p([\dab]+)/);
                let label = okey;
                if (mtch) {
                    let [fullstr, coll, ed, tnum, voln, pgn] = mtch;
                    const pgurl = `/catalog/${coll}/${ed}/${tnum}/pages/${voln}/${pgn}`;
                    label = (
                        <Link to={pgurl}>
                            Vol. {voln}, pg {pgn}
                        </Link>
                    );
                } else if (mtch2) {
                    // LCCW Texts
                    const pgtype =
                        myedbib?.resource_pagination_s === 'digpage'
                            ? 'pages'
                            : 'text';
                    let [fullstr, coll, tnum, pgn] = mtch2;
                    const pgurl = `/catalog/${coll}/main/${tnum}/${pgtype}/${pgn}`;
                    label = <Link to={pgurl}>pg {pgn}</Link>;
                } else if (okey.includes('-bib-')) {
                    label = okey.split('-bib-')[1].replace(/\-/g, ' ');
                    const ed = doc.edsig === 'main' ? '' : `/${doc.edsig}`;
                    label = (
                        <Link to={`/catalog/${doc.coll}${ed}/${doc.text_num}`}>
                            {label}
                        </Link>
                    );
                } else if ((m3 = okey.match(/[^\-]+-\d+-text-(.*)-text/))) {
                    label = m3[1].replace(/\-/g, '.');
                    label = (
                        <Link
                            to={`/catalog/${doc.coll}/${doc.edsig}/${doc.text_num}/text`}
                        >
                            {label}
                        </Link>
                    );
                } else {
                    console.warn('No match for okey, ', okey);
                }
                if (JSON.stringify(hitres) === '{}') {
                    hitres = 'No preview available.';
                } //  Sometimes hitres is an empty object because highlighting doesn't work
                // console.log('hit res', hitres);
                return (
                    <li
                        key={`${bibid}-hit-${oki}`}
                        data-solrid={okey}
                        className="cat-search-hit"
                    >
                        <span className="label">{label}: </span>
                        <span className="hit">
                            <HtmlCustom markup={hitres} />
                        </span>
                    </li>
                );
            })}
        </ol>
    );
}

/**
 * Gets the highlighted hit for a search result:
 *
 * // TODO: Generalize for other languages (see const lang = "bo" below)
 * // TODO: get SOLR highlighting to work properly for Tibetan / Unicode (see "else" block below)
 *
 * @param hit
 * @param pg
 * @param qry
 * @returns {JSX.Element}
 */
function getHit(hit, pg, qry) {
    // console.log("hit in get hit", hit);
    const lang = 'bo';
    const pgfield = `pg_${lang}`;
    let hit_text = '';
    qry = qry.replace(/"/g, '');
    if (Object.keys(hit).includes(pgfield)) {
        hit_text = hit[pgfield][0];
    } else if (Array.isArray(hit['content']) && hit['content'].length > 0) {
        hit_text = hit['content'][0];
    } else if (hit?.title?.length > 0) {
        hit_text = hit.title[0];
    } else {
        /**
         * This block is called when the SOLR highlighting field in the results is empty for a page,
         * Because SOLR highlighting does not "find" the query string. Seems to happen randomly, not sure why.
         * The following code uses the "pg_bo" field and manually highlights the search string and trims the
         * return results. Works but kludgy.
         */
        hit_text = pg[pgfield]?.length > 0 ? pg[pgfield][0] : '';
        if (hit_text?.includes(qry)) {
            let hitpts = hit_text.split(qry);
            if (hitpts && hitpts?.length > 0) {
                let stind = hitpts[0].length - 50;
                stind = hitpts[0].lastIndexOf('\u0F0B', stind);
                hitpts[0] = hitpts[0].substring(stind);
                hitpts[1] = hitpts[1].substring(0, 50);
                hit_text =
                    hitpts[0] + `<span class="hl">${qry}</span>` + hitpts[1];
            }
        }
    }
    return <>{ReactHtmlParser(hit_text)}</>; // Parse the HTML to get the highlight tag to render
}
